import React, { useState, useEffect } from "react";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
  DragStartEvent,
  DragOverEvent,
  DragEndEvent,
} from "@dnd-kit/core";
import SortableCard from "./OptimizedSortableCard";
import OptimizedKanbanColumn from "./OptimizedKanbanColumn";
import { useSelector, useDispatch } from "react-redux";
import { RootState, RootDispatch } from "../../store";
import toast from "react-hot-toast";
import KanbanBoardSkeleton from "../Skeleton/KanbanBoardSkeleton";
import type { IColumn } from "../../models/LeadData.model";
import {
  fetchAllColumns,
  fetchKanbanData,
  fetchLeadsByStatus,
  updateAssigneeRole,
  updateCardStatus,
  updateSourceTypes,
} from "../../store/slices/kanbanSlice";
import { isEqual } from "lodash";
interface OptimizedKanbanBoardProps {
  sanitizeFilters: (filters: any) => any;
}

const OptimizedKanbanBoard: React.FC<OptimizedKanbanBoardProps> = ({
  sanitizeFilters,
}) => {
  const dispatch: RootDispatch = useDispatch();
  const loading = useSelector((state: RootState) => state.kanban.loading);
  const columns = useSelector((state: RootState) => state.kanban.columns);
  const visibleColumns = useSelector(
    (state: RootState) => state.kanban.visibleColumns,
  );
  const { roles } = useSelector((state: RootState) => state.common);
  const columnLoading = useSelector(
    (state: RootState) => state.kanban.columnLoading,
  );
  const columnHasMore = useSelector(
    (state: RootState) => state.kanban.columnHasMore,
  );
  const filters = useSelector((s: RootState) => s.kanbanFilters);
  const columnPages = useSelector(
    (state: RootState) => state.kanban.columnPages,
  );
  const lastFilters = useSelector((s: RootState) => s.kanban.lastFilters);

  const sensors = useSensors(useSensor(PointerSensor));

  const [localColumns, setLocalColumns] = useState(columns);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [overColumnId, setOverColumnId] = useState<string | null>(null);
  const [activeCard, setActiveCard] = useState<any>(null);
  const [columnsLoaded, setColumnsLoaded] = useState(false);

  useEffect(() => {
    try {
      if (!columnsLoaded && Object.keys(columns).length === 0) {
        setColumnsLoaded(true);
        dispatch(fetchAllColumns()).unwrap();
      }
    } catch (error) {
      console.error("Failed to fetch columns:", error);
    }
  }, [dispatch, columns, columnsLoaded]);

  useEffect(() => {
    setLocalColumns(columns);
  }, [columns]);
  // Load more leads when scrolling to the bottom of a column
  const onLoadMore = async (status: string) => {
    const currentPage = columnPages[status] || 1;
    const hasMore = columnHasMore[status];

    if (!hasMore || columnLoading[status]) return;

    const sanitized = sanitizeFilters(filters);
    dispatch(
      fetchLeadsByStatus({
        status,
        page: currentPage + 1,
        limit: 20,
        filters: sanitized,
        append: true,
      }),
    );
  };

  useEffect(() => {
    const sanitized = sanitizeFilters(filters);
    const sanitizedLastFilters = sanitizeFilters(lastFilters ?? {});

    const fetchData = async () => {
      if (!lastFilters || !isEqual(sanitized, sanitizedLastFilters)) {
        try {
          dispatch(fetchKanbanData(sanitized));
          setColumnsLoaded(false);
        } catch (error) {
          console.error("Failed to fetch leads:", error);
        }
      }
    };

    fetchData();
  }, [dispatch, filters, lastFilters]);

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
    setActiveCard(event.active.data.current?.card);
  };

  const handleDragOver = (event: DragOverEvent) => {
    setOverColumnId(event.over?.data.current?.columnId || null);
  };

  const updateSourceType = async (leadId: string, sourceType: string) => {
    try {
      await dispatch(updateSourceTypes({ leadId, sourceType })).unwrap();
    } catch (error) {
      console.error("Error updating source type:", error);
    }
  };

  const onDragEnd = async ({
    cardId,
    sourceStatus,
    destinationStatus,
    currentIndex,
    destinationIndex,
  }: {
    cardId: string;
    sourceStatus: string;
    destinationStatus: string;
    currentIndex: number;
    destinationIndex: number;
  }) => {
    try {
      if (sourceStatus.toLowerCase() !== "cp") {
        await dispatch(
          updateCardStatus({
            leadId: cardId,
            currentStatus: sourceStatus,
            destinationStatus,
            currentIndex,
            destinationIndex,
          }),
        );
      } else {
        toast.error("Leads in 'CP' status cannot be moved.");
      }

      if (sourceStatus !== destinationStatus) {
        toast.success(
          `Lead moved from "${sourceStatus}" to "${destinationStatus}"`,
        );
      }
    } catch (err) {
      toast.error("Error updating card status");
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);
    setOverColumnId(null);
    setActiveCard(null);

    if (!over || !active?.data?.current || !over?.data?.current) return;

    const cardId = active.id as string;
    const sourceColId = active.data.current.columnId;
    const destColId = over.data.current.columnId;

    if (!sourceColId || !destColId) return;

    const sourceColumn = localColumns[sourceColId];
    const destColumn = localColumns[destColId];
    const draggedCard = sourceColumn.cards.find((c: any) => c._id === cardId);
    if (!draggedCard) return;

    const newSourceCards = sourceColumn.cards.filter(
      (c: any) => c._id !== cardId,
    );
    let newDestCards = [...destColumn.cards];

    const overCardId = over.id as string;
    const destinationIndex = destColumn.cards.findIndex(
      (c: any) => c._id === overCardId,
    );
    const currentIndex = sourceColumn.cards.findIndex(
      (c: any) => c._id === cardId,
    );

    if (sourceColId === destColId) {
      newDestCards.splice(currentIndex, 1);
      newDestCards.splice(destinationIndex, 0, draggedCard);
    } else {
      if (destinationIndex >= 0) {
        newDestCards.splice(destinationIndex, 0, draggedCard);
      } else {
        newDestCards.push(draggedCard);
      }
    }

    if (
      localColumns[sourceColId].name !== "cp" &&
      localColumns[destColId].name !== "cp"
    ) {
      setLocalColumns((prev) => ({
        ...prev,
        [sourceColId]: { ...prev[sourceColId], cards: newSourceCards },
        [destColId]: { ...prev[destColId], cards: newDestCards },
      }));

      try {
        await onDragEnd({
          cardId,
          sourceStatus: localColumns[sourceColId].name,
          destinationStatus: localColumns[destColId].name,
          currentIndex,
          destinationIndex,
        });
      } catch (err) {
        setLocalColumns(columns);
      }
    } else {
      toast.error("Leads in 'CP' status cannot be moved.");
    }
  };

  const updateAssignee = async (
    leadId: string,
    oldAssigneeId: string,
    newAssignee: string,
    index: number,
    columnIndex: string,
  ) => {
    if (!newAssignee) {
      toast.error("Please select an assignee.");
      return;
    }

    if (oldAssigneeId !== newAssignee) {
      try {
        await dispatch(
          updateAssigneeRole({
            leadId,
            assigneeId: newAssignee,
            index,
            columnIndex,
          }),
        );
      } catch (error: any) {
        toast.error("Error updating Assignee: " + error);
      }
    }
  };

  if (loading) return <KanbanBoardSkeleton />;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <DragOverlay>
        {activeCard ? (
          <div className="pointer-events-none opacity-80">
            <SortableCard
              card={activeCard}
              index={0}
              columnId={"overlay"}
              updateSourceType={updateSourceType}
              updateAssignee={updateAssignee}
              roles={roles}
            />
          </div>
        ) : null}
      </DragOverlay>

      <div className="w-full max-w-full overflow-x-auto overflow-y-hidden">
        <div className="flex p-4 space-x-8 min-w-max">
          {Object.values(localColumns)
            .filter(
              (col): col is IColumn =>
                !!col && !!col.id && visibleColumns[col.id],
            )
            .map((col) => {
              return (
                <div key={col.id} className="w-[300px] shrink-0">
                  <OptimizedKanbanColumn
                    column={col}
                    activeId={activeId}
                    overColumnId={overColumnId}
                    updateSourceType={updateSourceType}
                    updateAssignee={updateAssignee}
                    roles={roles}
                    isLoading={columnLoading[col.name]}
                    onLoadMore={() => onLoadMore(col.name)}
                    hasMore={columnHasMore[col.name]}
                  />
                </div>
              );
            })}
        </div>
      </div>
    </DndContext>
  );
};

export default OptimizedKanbanBoard;
